import * as React from 'react';
import { useMemo } from 'react';
import type { ReviewTargets } from '@gcv/shared';
import { Box } from '@mui/material';

import { useComponent } from 'hooks';
import type { FilterSelectOption } from 'ui';
import { FilterSelect, FilterSelectButton, InputLabel, Tooltip } from 'ui';
import { DataTransformer } from 'util/data.util';
import {
  ACCOUNT_NUMBERS_LABEL,
  getReviewTargetsFromSelection
} from './muo-account-number-location-filter.util';

interface Props extends Record<string, unknown> {
  label?: string;
  onChange?: (targets: ReviewTargets) => void;
  accounts: FilterSelectOption[];
  required?: boolean;
  viewOnly?: boolean;
  type: 'dropdown' | 'dropdown-auto-apply';
  tooltip?: string;
}

export const MuoAccountNumberLocationFilter: React.FC<Props> = useComponent((props) => {
  const [filterToggle, setFilterToggle] = React.useState(false);
  const [currentOptions, setCurrentOptions] = React.useState<FilterSelectOption[]>(
    JSON.parse(JSON.stringify(props.accounts))
  );
  const [appliedOptions, setAppliedOptions] = React.useState<FilterSelectOption[]>(
    JSON.parse(JSON.stringify(props.accounts))
  );

  const toggleFilter = () => {
    setFilterToggle(!filterToggle);
  };

  const onApply = (data: FilterSelectOption[]) => {
    setCurrentOptions(data);
    setAppliedOptions(JSON.parse(JSON.stringify(data)));
    const selected = new DataTransformer().selected(data);
    const targets = getReviewTargetsFromSelection(selected, data);
    if (props.onChange) {
      props.onChange(targets);
    }
    if (props.type !== 'dropdown-auto-apply') {
      setFilterToggle(false);
    }
  };

  const onCancel = () => {
    if (props.type !== 'dropdown-auto-apply') {
      setCurrentOptions(appliedOptions);
    }
    setTimeout(() => setFilterToggle(false), 1);
  };

  const selectedAccounts = useMemo(() => {
    const accountNumberFilter = appliedOptions.find((o) => o.value === ACCOUNT_NUMBERS_LABEL);

    if (accountNumberFilter) {
      return accountNumberFilter.children ?? [];
    } else {
      return appliedOptions.flatMap((o) => o.children ?? []);
    }
  }, [appliedOptions]);

  return (
    <Box sx={{ position: 'relative' }}>
      {props.label && (
        <Box sx={{ mb: '6px' }}>
          <InputLabel
            name="account-number-filter-label"
            label={props.label}
            required={props.required}
            viewOnly={props.viewOnly}
          />
        </Box>
      )}
      {props.tooltip ? (
        <Tooltip title={props.tooltip} placement="top" display="inline">
          <FilterSelectButton
            filterToggle={filterToggle}
            toggleFilter={toggleFilter}
            selectedFilters={selectedAccounts}
            noMargin
            viewOnly={props.viewOnly}
            filterHeader="Accounts"
            showZeroSelected={props.type === 'dropdown-auto-apply'}
            type={props.type}
          />
        </Tooltip>
      ) : (
        <FilterSelectButton
          filterToggle={filterToggle}
          toggleFilter={toggleFilter}
          selectedFilters={selectedAccounts}
          noMargin
          viewOnly={props.viewOnly}
          filterHeader="Accounts"
          showZeroSelected={props.type === 'dropdown-auto-apply'}
          type={props.type}
        />
      )}

      {filterToggle ? (
        <FilterSelect
          transform={'raw'}
          options={currentOptions}
          onApply={onApply}
          onCancel={onCancel}
          config={{
            title: 'Accounts',
            height: '250px',
            emitAllOnEmpty: true,
            emitExcludeParent: false,
            clearChildren: { isHidden: false },
            count: { isHidden: true },
            search: { isHidden: true },
            selectAllClearAll: { isHidden: false, position: 'top' },
            showSelectedOnly: { isHidden: true },
            showHidden: { isHidden: true, label: '' },
            type: props.type
          }}
        />
      ) : null}
    </Box>
  );
});
